/* eslint-disable @typescript-eslint/no-explicit-any */
import { IUserConnection } from 'interfaces/redux/IUserConnection';
import { handleFaGrantNotification, updatePatientTaskMultiUser } from 'actions/action-socket';
import { useCallback, useEffect, useRef } from 'react';
import { useSelector, RootStateOrAny } from 'react-redux';
import { Socket, io } from 'socket.io-client';
import { debounce } from 'lodash';
import {
  fetchHiddenQuestions,
  fetchPatient,
  refreshPatientPhones,
  workListChanged,
} from 'actions/action-patient';
import { notifySuccess } from 'actions/action-notifications';
import { fetchTaskCounts, updateTaskByIdsFCSelfService } from 'actions/action-tasks';
import { IPayloadFCSocket } from '../interfaces/redux/IPayloadFCSocket';
import { getSocket } from '../services/socket-manager';
import {
  EventListenersSocket as listeners,
  EventEmitsSocket as emits,
} from '../interfaces/redux/ISocket';
import { useAppDispatch } from './useAppDispatch';
import { IFaGrantNotification } from '../interfaces/redux/IFaGrantNotification';
import { useTypedSelector } from './use-typed-selector';
import { IAllOnlineClientsData, IPatientCount } from '../interfaces/redux/IAllOnlineClientsData';

declare global {
  interface Window {
    socketConnections?: Record<string, Socket>;
  }
}

// Debug utilities for Socket.IO monitoring
const DEBUG_SOCKET = process.env.REACT_APP_DEBUG_SOCKET === 'true';

const debugLog = (message: string, data?: any) => {
  if (DEBUG_SOCKET) {
    const timestamp = new Date().toISOString();
    // eslint-disable-next-line no-console
    console.log(`🔌 [${timestamp}] ${message}`, data || '');
  }
};

const debugError = (message: string, error?: any) => {
  if (DEBUG_SOCKET) {
    const timestamp = new Date().toISOString();
    // eslint-disable-next-line no-console
    console.error(`❌ [${timestamp}] ${message}`, error || '');
  }
};

const createDebugSocket = (socket: Socket) => {
  if (!DEBUG_SOCKET) return socket;

  const originalEmit = socket.emit.bind(socket);
  const originalOn = socket.on.bind(socket);
  const originalOnce = socket.once.bind(socket);

  socket.emit = (event: string, ...args: any[]) => {
    debugLog(`📤 EMIT ${event}`, args);
    return originalEmit(event, ...args);
  };

  socket.on = (event: string, callback: (...args: any[]) => void) => {
    function onHandler(this: any, ...args: any[]) {
      debugLog(`📥 RECEIVE ${event}`, args);
      callback.apply(this, args);
    }
    return originalOn(event, onHandler as any);
  };

  socket.once = (event: string, callback: (...args: any[]) => void) => {
    function onceHandler(this: any, ...args: any[]) {
      debugLog(`📥 RECEIVE (once) ${event}`, args);
      callback.apply(this, args);
    }
    return originalOnce(event, onceHandler as any);
  };

  return socket;
};

interface IUseSocket {
  socketReducer: {
    socket: Socket | null;
    userConnections: object;
  };
  selectedPatientId: number;
}

const defaultConfig = {
  transports: ['websocket'],
  withCredentials: true,
  reconnection: true,
  reconnectionAttempts: 3,
  reconnectionDelay: 1000,
  timeout: 5000,
  forceNew: false, // Prevents creating new connections
};
const socketInstances = new Map<string, Socket>();
const connected = 'connect';

export const useMultiUserSocket = (userId: number) => {
  const selectedPatientId = useSelector((state: RootStateOrAny) => state.selectedPatientId);
  const customerId = useSelector((state: RootStateOrAny) => state.filters.selectedCustomerId);
  const selectedTask = useSelector((state: RootStateOrAny) => state.selectedTaskId);
  const dispatch = useAppDispatch();

  const tabId = useRef(`tab_${Date.now()}_${Math.random()}`).current;
  const lastPresenceUpdate = useRef<number | null>(null);
  const eventsReceived = useRef(0);
  const eventsSent = useRef(0);

  const selectedPatientIdRef = useRef(selectedPatientId);
  const customerIdRef = useRef(customerId);

  useEffect(() => {
    selectedPatientIdRef.current = selectedPatientId;
  }, [selectedPatientId]);

  useEffect(() => {
    customerIdRef.current = customerId;
  }, [customerId]);

  const validateUserConnections = (conns: any[]): IUserConnection[] => {
    if (!Array.isArray(conns)) {
      debugError('Invalid user connections data - not an array', conns);
      return [];
    }

    const normalized = conns
      .filter((conn: any) => {
        if (!conn || typeof conn !== 'object') {
          debugError('Invalid connection object', conn);
          return false;
        }

        if (conn.id == null || !conn.name || conn.room == null) {
          debugError('Missing required connection fields', conn);
          return false;
        }

        return true;
      })
      .map((conn: any) => {
        const room = Number(conn.room);
        const id = Number(conn.id);
        const name = String(conn.name);

        let patientArray = Array.isArray(conn.patient) ? conn.patient : [];
        if (patientArray.length > 0 && Array.isArray(patientArray[0])) {
          patientArray = patientArray.flat();
        }

        const patient = patientArray.map((p: any) => ({
          patientId: Number(p?.patientId),
          tasks: Array.isArray(p?.tasks) ? p.tasks.map((t: any) => Number(t)) : [],
        }));

        return { ...conn, id, name, room, patient } as unknown as IUserConnection;
      });

    return normalized as unknown as IUserConnection[];
  };

  const sendConnections = (conns: any) => {
    try {
      const validatedConns = validateUserConnections(conns);
      const numericCustomerId = Number(customerIdRef.current);
      const numericSelectedPatientId = Number(selectedPatientIdRef.current);

      const filteredConns = validatedConns
        .filter(
          (conn: any) =>
            Number(conn.room) === numericCustomerId &&
            Number(conn.id) !== Number(userId) &&
            conn.patient?.some((x: any) => Number(x.patientId) === numericSelectedPatientId),
        )
        .reduce((acc: any, item: any) => ({ ...acc, [item.id]: item.name }), {});

      window.postMessage({ source: 'multiuserConnections', conns: filteredConns }, '*');
    } catch (error) {
      debugError('Error processing connections', error);
    }
  };

  const sendPatientCounts = (patientCounts: Record<string, IPatientCount>) => {
    try {
      const currentSelectedPatientId = selectedPatientIdRef.current;
      const currentPatientData = patientCounts[String(currentSelectedPatientId)];

      window.postMessage(
        {
          source: 'patientViewerCounts',
          patientId: currentSelectedPatientId,
          data: currentPatientData || {
            userCount: 1,
            hasMultipleUsers: false,
            socketCount: 1,
            userIds: [userId],
            socketIds: [],
          },
        },
        '*',
      );
    } catch (error) {
      debugError('Error sending patient counts', error);
    }
  };

  let multiUserSocket = socketInstances.get(tabId);

  if (!multiUserSocket) {
    debugLog(`🔌 Creating new socket for tab ${tabId}`);
    multiUserSocket = getSocket('/');
    multiUserSocket = createDebugSocket(multiUserSocket);
    socketInstances.set(tabId, multiUserSocket);

    if (typeof window !== 'undefined') {
      if (!window.socketConnections) {
        window.socketConnections = {};
      }
      window.socketConnections[tabId] = multiUserSocket;
    }
  } else {
    debugLog(`⚠️ Socket already exists for tab ${tabId}, reusing...`);
  }

  const socket = multiUserSocket as Socket;

  const emitPresence = useCallback(() => {
    if (!socket || selectedPatientId < 1 || customerId == null) return;

    const doEmit = () => {
      eventsSent.current += 2;
      socket.emit(emits.roomAccessesCustomer, customerId);
      socket.emit(emits.patientAccesses, [
        {
          patientId: selectedPatientId,
          tasks: [selectedTask],
        },
      ]);
    };

    if (socket.connected) {
      doEmit();
    } else {
      socket.once(connected, doEmit);
    }
  }, [socket, customerId, selectedPatientId, selectedTask]);

  if (!socket.hasListeners(connected)) {
    socket.on(connected, () => {
      emitPresence();
    });
  }

  if (!socket.hasListeners('disconnect')) {
    socket.on('disconnect', () => {});
  }

  if (!socket.hasListeners('connect_error')) {
    socket.on('connect_error', () => {});
  }

  if (!socket.hasListeners('reconnect')) {
    socket.on('reconnect', () => {
      emitPresence();
    });
  }

  if (!socket.hasListeners('reconnect_attempt')) {
    socket.on('reconnect_attempt', () => {});
  }

  if (!socket.hasListeners('reconnect_error')) {
    socket.on('reconnect_error', () => {});
  }

  if (!socket.hasListeners(listeners.allOnlineClients)) {
    socket.on(listeners.allOnlineClients, (data: IAllOnlineClientsData) => {
      eventsReceived.current += 1;
      lastPresenceUpdate.current = Date.now();

      const { connections, patientCounts } = data;
      const validatedConns = validateUserConnections(connections);

      dispatch(updatePatientTaskMultiUser(validatedConns));
      sendPatientCounts(patientCounts);
      sendConnections(validatedConns);
    });
  }

  useEffect(() => {
    emitPresence();
  }, [customerId, selectedPatientId, selectedTask, emitPresence]);

  useEffect(() => {
    const onFocus = () => emitPresence();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') emitPresence();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [emitPresence]);

  useEffect(() => {
    return () => {
      if (socket) {
        socket.disconnect();
        socketInstances.delete(tabId);
        if (typeof window !== 'undefined' && window.socketConnections) {
          delete window.socketConnections[tabId];
        }
      }
    };
  }, [tabId, socket]);

  return {};
};

export const useRulesEngineSocket = () => {
  const dispatch = useAppDispatch();
  const rulesEngineSocket = useSelector(
    (state: RootStateOrAny) => state.socketReducer.rulesEngineSocket,
  );
  const sidebarTaskType = useSelector((state: RootStateOrAny) => state.sidebarTaskType);
  const selectedPatientId = useSelector((state: RootStateOrAny) => state.selectedPatientId);
  const debouncePatientHiddenQuestionHandler = debounce(() => {
    dispatch(fetchHiddenQuestions());
  }, 1000);
  const debounceUpdateTaskCounts = debounce(() => {
    dispatch(fetchTaskCounts());
  }, 3000);
  const debounceUpdateTaskWorkList = debounce(() => {
    dispatch(workListChanged());
  }, 3000);
  const debounceUpdatePatient = debounce(patientId => {
    dispatch(fetchPatient(patientId));
  }, 3000);

  if (rulesEngineSocket instanceof Socket) {
    if (!rulesEngineSocket.connected) rulesEngineSocket.connect();

    if (!rulesEngineSocket.hasListeners(listeners.questionShow)) {
      rulesEngineSocket.on(listeners.questionShow, (notification: { patient_id: number }) => {
        if (notification.patient_id === selectedPatientId) {
          debouncePatientHiddenQuestionHandler();
        }
      });
    }

    if (!rulesEngineSocket.hasListeners(listeners.questionHide)) {
      rulesEngineSocket.on(listeners.questionHide, (notification: { patient_id: number }) => {
        if (notification.patient_id === selectedPatientId) {
          debouncePatientHiddenQuestionHandler();
        }
      });
    }

    if (!rulesEngineSocket.hasListeners(listeners.newTask)) {
      rulesEngineSocket.on(
        listeners.newTask,
        (notification: { patient_id: number; task_type: string }) => {
          if (notification.patient_id === selectedPatientId) {
            debounceUpdateTaskCounts();
            debounceUpdatePatient(selectedPatientId);
            if (notification.task_type === sidebarTaskType) {
              debounceUpdateTaskWorkList();
            }
          }
        },
      );
    }
  }

  return {};
};

export const useFaSocket = () => {
  const dispatch = useAppDispatch();
  const faSocket = useSelector((state: RootStateOrAny) => state.socketReducer.faSocket);
  const customerId = useTypedSelector(state => state.filters.selectedCustomerId);
  const debounceHandleFaGrantNotification = debounce(notification => {
    dispatch(
      handleFaGrantNotification({
        socket: faSocket,
        notification,
        forceTaskType: 'FA',
      }),
    );
  }, 10000);

  if (faSocket instanceof Socket) {
    if (!faSocket.connected) faSocket.connect();

    if (!faSocket.hasListeners(listeners.notifyFaGrant)) {
      faSocket.on(listeners.notifyFaGrant, (notification: IFaGrantNotification) => {
        if (notification.customer_id === customerId) {
          debounceHandleFaGrantNotification(notification);
        }
      });
    }
  }

  return {};
};

export const useFCSocket = () => {
  const dispatch = useAppDispatch();
  const fcSocket = useSelector((state: RootStateOrAny) => state.socketReducer.fcSocket);
  const selectedPatientId = useSelector((state: RootStateOrAny) => state.selectedPatientId);
  const customerId = useSelector((state: RootStateOrAny) => state.filters.selectedCustomerId);

  const refreshPatientPhonesHandler = useCallback(
    (payload: IPayloadFCSocket) => {
      if (payload.patientId === selectedPatientId) {
        dispatch(refreshPatientPhones(selectedPatientId));
        dispatch(notifySuccess(`Patient ${payload.name} replied to the sms`));
      }
    },
    [selectedPatientId, dispatch],
  );

  const refreshUpdateFCSS = useCallback(
    (payload: IPayloadFCSocket) => {
      if (payload.patientId === selectedPatientId && payload?.taskIds?.length) {
        dispatch(updateTaskByIdsFCSelfService(payload.taskIds));
        dispatch(
          notifySuccess(`Patient ${payload.name} responded to FC Self-Service questionnaire`),
        );
      }
    },
    [selectedPatientId, dispatch],
  );

  if (fcSocket instanceof Socket) {
    if (!fcSocket.connected) fcSocket.connect();

    if (!fcSocket.hasListeners(connected)) {
      fcSocket.on(connected, () => {
        fcSocket.emit(emits.roomAccessesCustomer, customerId);
      });
    }

    if (!fcSocket.hasListeners(listeners.updatePhones)) {
      fcSocket.on(listeners.updatePhones, refreshPatientPhonesHandler);
    }

    if (!fcSocket.hasListeners(listeners.updateStatusFCSS)) {
      fcSocket.on(listeners.updateStatusFCSS, refreshUpdateFCSS);
    }
  }

  useEffect(() => {
    if (fcSocket instanceof Socket) {
      fcSocket.emit(emits.roomAccessesCustomer, customerId);
    }
  }, [customerId, fcSocket]);

  useEffect(() => {
    if (fcSocket instanceof Socket) {
      if (selectedPatientId < 1) return;

      if (!fcSocket.connected) fcSocket.connect();

      if (fcSocket.hasListeners(listeners.updatePhones)) {
        fcSocket.removeListener(listeners.updatePhones);
        fcSocket.on(listeners.updatePhones, refreshPatientPhonesHandler);
      }

      if (fcSocket.hasListeners(listeners.updateStatusFCSS)) {
        fcSocket.removeListener(listeners.updateStatusFCSS);
        fcSocket.on(listeners.updateStatusFCSS, refreshUpdateFCSS);
      }
    }
  }, [selectedPatientId, fcSocket, refreshPatientPhonesHandler, refreshUpdateFCSS]);

  return {};
};

export const useSocket = (userId: number) => {
  useMultiUserSocket(userId);
  return {};
};
